/*
 * The membership plans page.
 *
 * An asset rather than a script inside the section, because the theme does not
 * re-run a section's inline script after an SPA swap — it only re-executes
 * scripts for lazily inserted homepage sections. Reached through a link rather
 * than a fresh load, the page rendered and then did nothing: no billing toggle,
 * no sale price, the add-to-cart buttons falling back to a full page post, and
 * the cancelled-membership check never asked. The styles survive, which is what
 * makes it look finished.
 *
 * So it initialises the way everything else in this theme that must survive a
 * transition does: on load, on od:after-swap, and on pushstate and popstate.
 */
(function () {
  "use strict";

  function init() {
  (function () {
    var root = document.querySelector('[data-od-plans]');
    if (!root) return false;
    if (root.dataset.odBound === '1') return true;
    root.dataset.odBound = '1';

    /*
     * The shop caches the customer's tier in sessionStorage so every page does
     * not have to ask for it. Changing a membership makes that cache wrong,
     * and no page load clears it: sessionStorage survives a reload and a
     * redirect alike. A new Patron who walked to Coming Soon was therefore
     * still being offered Patron, from a cache that had five minutes left to
     * run.
     *
     * The your-membership page has always done this. This page never did,
     * which is why the same upgrade behaved differently depending on where it
     * was made.
     *
     * The access key too, not only the tier. What a tier may do is the
     * policy's to say, and it is cached under its own name.
     */
    function forgetMemberTier(nextTier) {
      try {
        sessionStorage.removeItem('od-member-tier-v1');
        sessionStorage.removeItem('od-coming-soon-access-v1');
        sessionStorage.removeItem('od-active-sale-v3');
      } catch (error) {
        /* private mode: nothing was cached to begin with */
      }

      /* Anything already on the page that gates on membership hears about it
         now rather than after the redirect, so the dialog is not sitting over
         a page that still thinks they are a Collector. A listener with no tier
         asks the policy again. */
      try {
        window.dispatchEvent(
          new CustomEvent('od:membership-changed', {
            detail: typeof nextTier === 'string' ? { tier: nextTier } : {}
          })
        );
      } catch (error) {
        /* CustomEvent is available everywhere we support; never worth failing
           a completed change over. */
      }
    }

    /*
     * Changing tier from this page.
     *
     * It used to be a link to manage_url, which defaults to this page: pressing
     * it went nowhere, jumped to the top, and read as broken. A member who is
     * already signed in gets the same flow the your-membership page gives them
     * — the same question, the same endpoint, the same wording — rather than
     * being sent somewhere else to do it.
     */
    (function wireTierChange() {
      var CHANGE_URL = '/apps/library/membership/change-tier';
      /*
       * A transformed ancestor becomes the containing block for
       * position:fixed, and this theme transforms <main> mid-transition. On
       * the other page that left the dialog stranded halfway down the page, so
       * it is lifted to <body>.
       *
       * Which means clearing up after the last visit: an SPA swap brings a
       * fresh section with a fresh dialog inside it, while the one moved out
       * on the previous visit is still sitting on <body>. Without this they
       * accumulate, and the stale one is the one a document-wide lookup finds.
       */
      var dialog = root.querySelector('[data-od-confirm]');

      if (dialog) {
        var stale = document.querySelectorAll('body > .od-plans__tierdialog[data-od-confirm]');
        for (var st = 0; st < stale.length; st++) {
          if (stale[st] !== dialog && stale[st].parentNode) {
            stale[st].parentNode.removeChild(stale[st]);
          }
        }
        if (dialog.parentNode !== document.body) document.body.appendChild(dialog);
      } else {
        dialog = document.querySelector('body > .od-plans__tierdialog[data-od-confirm]');
      }

      var title = dialog && dialog.querySelector('[data-od-confirm-title]');
      var body = dialog && dialog.querySelector('[data-od-confirm-body]');
      var facts = dialog && dialog.querySelector('[data-od-confirm-facts]');
      var go = dialog && dialog.querySelector('[data-od-confirm-go]');
      var pending = null;

      function closeDialog() {
        if (!dialog) return;
        dialog.classList.remove('is-open');
        pending = null;
        window.setTimeout(function () { dialog.hidden = true; }, 180);
      }

      function ask(options) {
        /*
         * Fail closed. When this dialog could not be found on the other page
         * the change went through unasked and the letter was sent — so if
         * anything here is missing, a plain browser prompt asks instead.
         * Nobody's membership changes because they clicked once.
         */
        if (!dialog || !title || !body || !facts || !go) {
          var asked = (options.isUpgrade ? 'Become a ' : 'Change to ') + options.name + '?';
          if (window.confirm(asked)) options.onConfirm();
          return;
        }

        pending = options.onConfirm;

        var lines = [];
        if (options.isUpgrade) {
          title.textContent = 'Become a ' + options.name + '?';
          body.textContent =
            'Everything a ' + options.name + ' receives becomes available to you straight away.';
          lines.push('You are not charged anything today.');
          if (options.price) lines.push('Your next payment will be ' + options.price + '.');
          lines.push('You can move back down at any time.');
          go.textContent = 'Yes, become a ' + options.name;
        } else {
          title.textContent = 'Change to ' + options.name + '?';
          body.textContent =
            'Nothing changes today. You keep everything your current membership includes until the end of the period you have already paid for.';
          lines.push('From then you will be a ' + options.name + '.');
          if (options.price) lines.push('Your new rate will be ' + options.price + '.');
          lines.push('You can change your mind before then.');
          go.textContent = 'Yes, change to ' + options.name;
        }

        facts.innerHTML = '';
        for (var i = 0; i < lines.length; i++) {
          var li = document.createElement('li');
          li.textContent = lines[i];
          facts.appendChild(li);
        }

        dialog.hidden = false;
        void dialog.offsetWidth;
        dialog.classList.add('is-open');
        go.focus();
      }

      if (dialog && dialog.dataset.odConfirmBound !== '1') {
        dialog.dataset.odConfirmBound = '1';
        dialog.addEventListener('click', function (event) {
          if (event.target.hasAttribute('data-od-confirm-cancel')) closeDialog();
          if (event.target.hasAttribute('data-od-confirm-go')) {
            var run = pending;
            closeDialog();
            if (run) run();
          }
        });
        document.addEventListener('keydown', function (event) {
          /* isConnected, because a listener from a previous visit closes over
             a dialog that has since been removed. */
          if (event.key === 'Escape' && dialog && dialog.isConnected && !dialog.hidden) {
            closeDialog();
          }
        });
      }

      /* The card shows whichever interval the toggle is on, so the price the
         member is looking at is the one to quote back at them. */
      function priceFor(button) {
        var card = button.closest ? button.closest('[data-tier]') : null;
        if (!card) return '';
        var showing = card.querySelector(
          root.dataset.interval === 'monthly' ? '[data-od-price-monthly]' : '[data-od-price-annual]'
        );
        var amount = showing && showing.querySelector('.od-plans__amount');
        return amount ? amount.textContent.trim() : '';
      }

      var buttons = root.querySelectorAll('[data-od-change-tier]');
      for (var b = 0; b < buttons.length; b++) {
        (function (button) {
          if (button.dataset.odBound === '1') return;
          button.dataset.odBound = '1';

          var original = button.textContent.trim();

          button.addEventListener('click', function () {
            var tier = button.dataset.odChangeTier;
            var name = button.dataset.odTierName || tier;
            var isUpgrade = button.dataset.odRelation === 'upgrade';

            ask({
              isUpgrade: isUpgrade,
              name: name,
              price: priceFor(button),
              onConfirm: function () {
                button.disabled = true;
                button.textContent = isUpgrade ? 'Upgrading...' : 'Changing...';

                window
                  .fetch(CHANGE_URL, {
                    method: 'POST',
                    credentials: 'same-origin',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ tier: tier })
                  })
                  .then(function (response) {
                    return response
                      .json()
                      .catch(function () { return null; })
                      .then(function (data) { return { status: response.status, data: data }; });
                  })
                  .then(function (result) {
                    if (result.status === 401) {
                      button.disabled = false;
                      button.textContent = original;
                      window.alert('Please sign in again to change your membership.');
                      return;
                    }

                    if (!result.data || result.data.ok !== true) {
                      button.disabled = false;
                      button.textContent = original;
                      window.alert(
                        (result.data && result.data.error) ||
                          'We could not change your membership just now. Please try again, or write to us at info@operadepot.com.'
                      );
                      return;
                    }

                    /* Before anything else, because the cached tier is now
                       wrong and the dialog is about to invite them to leave. */
                    forgetMemberTier(result.data && result.data.toTier);

                    /*
                     * The page was rendered from membership state that has now
                     * moved, and re-rendering the cards by hand would mean
                     * duplicating the tier logic here and getting to disagree
                     * with Liquid about it.
                     *
                     * So it leaves the page rather than reloading it. A reload
                     * would answer the stale-state problem, but it would leave
                     * somebody who has just upgraded sitting on the page that
                     * sells memberships, which is now the one page with nothing
                     * left to offer them. Home is where they can use what they
                     * have just bought.
                     *
                     * Either way it waits for the dialog to be closed, because
                     * a downgrade leaves the old tier on screen and the dialog
                     * is the only thing explaining why.
                     *
                     * routes.root rather than "/", so a locale-prefixed
                     * storefront lands on its own home page.
                     */
                    var reload = function () {
                      var home =
                        (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
                      window.location.assign(home);
                    };

                    if (window.ODMembershipDone) {
                      button.textContent = result.data.appliedImmediately
                        ? 'You are now a ' + name
                        : 'Changing to ' + name;
                      window.ODMembershipDone.show(result.data, { root: root, onClose: reload });
                    } else {
                      button.textContent = result.data.appliedImmediately
                        ? 'You are now a ' + name
                        : 'Changing to ' + name;
                      window.setTimeout(reload, 1800);
                    }
                  })
                  .catch(function () {
                    button.disabled = false;
                    button.textContent = original;
                    window.alert('Something went wrong reaching us. Please check your connection and try again.');
                  });
              }
            });
          });
        })(buttons[b]);
      }
    })();

    var options = root.querySelectorAll('[data-od-interval]');

    function apply(interval) {
      root.dataset.interval = interval;

      for (var i = 0; i < options.length; i++) {
        var pressed = options[i].dataset.odInterval === interval;
        options[i].setAttribute('aria-pressed', pressed ? 'true' : 'false');
      }

      // The hidden forms are display:none, but a disabled control is what keeps
      // them out of submission and out of the tab order entirely.
      var forms = root.querySelectorAll('[data-od-form-monthly], [data-od-form-annual]');
      for (var j = 0; j < forms.length; j++) {
        var isMonthly = forms[j].hasAttribute('data-od-form-monthly');
        var active = isMonthly === (interval === 'monthly');
        var button = forms[j].querySelector('button');
        if (button && !button.dataset.odUnavailable) button.disabled = !active;
      }
    }

    /* Add to the cart the way the rest of the theme does.
       A native form post to /cart/add is a full page load, which on an SPA
       theme is the one thing the whole journey avoids. Alpine.store("cart")
       .addToCart posts to cart/add.js, refreshes the mini cart and leaves the
       page where it is: the same call snippets/product-actions-button.liquid
       makes. Falls back to submitting the form if the store is not there, so
       the worst case is the page load we had before rather than a dead
       button. */
    var cartForms = root.querySelectorAll('[data-od-form-monthly], [data-od-form-annual]');
    for (var f = 0; f < cartForms.length; f++) {
      /* Claimed, so the cart guard's fallback leaves it alone. */
      cartForms[f].dataset.odCartBound = '1';

      cartForms[f].addEventListener('submit', function (event) {
        var form = event.currentTarget;
        var store = window.Alpine && window.Alpine.store && window.Alpine.store('cart');
        if (!store || typeof store.addToCart !== 'function') return;

        event.preventDefault();

        var props = {};
        new FormData(form).forEach(function (value, key) { props[key] = value; });

        var button = form.querySelector('button[type="submit"]');

        /* The theme swaps the label rather than showing a spinner. Without it
           a tap does nothing visible, because the cart no longer reloads the
           page. */
        if (button) {
          button.dataset.odLabel = button.dataset.odLabel || button.textContent;
          button.disabled = true;
          button.textContent = root.dataset.odAddingLabel || 'Adding...';
        }

        try {
          store.addToCart(props);
        } catch (error) {
          if (button) {
            button.disabled = false;
            button.textContent = button.dataset.odLabel;
          }
          form.submit();
          return;
        }

        /* apply() owns which buttons are enabled, so it restores them rather
           than this doing it directly: re-enabling by hand would make the
           hidden interval's button tabbable again, and could revive a plan
           marked unavailable for having no selling plan. */
        window.setTimeout(function () {
          if (button && button.dataset.odLabel) button.textContent = button.dataset.odLabel;
          apply(root.dataset.interval || 'annual');
        }, 1200);
      });
    }

    // Remember which buttons were already unavailable, so re-enabling on a
    // switch cannot make a plan buyable that has no selling plan attached.
    var allButtons = root.querySelectorAll('form button');
    for (var k = 0; k < allButtons.length; k++) {
      if (allButtons[k].disabled) allButtons[k].dataset.odUnavailable = '1';
    }

    for (var m = 0; m < options.length; m++) {
      options[m].addEventListener('click', function (event) {
        apply(event.currentTarget.dataset.odInterval);
      });
    }

    apply(root.dataset.interval || 'annual');

    /* A running membership sale, shown on the yearly cards.
       Read from the sale rather than written into the page, so a sale that
       starts or ends does not need the theme republished. */
    /*
     * Learned from a price Liquid already rendered on this page, so a rewritten
     * price is formatted exactly like the one struck through beside it. The
     * store sells into 237 countries through the currency picker, and a
     * hardcoded dollar sign printed the right number with the wrong symbol for
     * every one of them but the US.
     *
     * Learn before rewriting anything: the element it reads is the element it
     * is about to overwrite.
     */
    function money(cents) {
      return '$' + (cents / 100).toFixed(2);
    }

    function moneyFor(card) {
      var amount = card && card.querySelector('[data-od-amount]');
      var cents = card && card.dataset ? card.dataset.annualCents : null;
      var learned =
        window.ODMoney && amount ? window.ODMoney.learn(cents, amount.textContent) : null;
      return learned || money;
    }

    /*
     * What the yearly toggle promises.
     *
     * "Save 17%" was written into the theme, and it was the standing saving:
     * a year's membership against twelve months of the monthly price. The
     * introductory discount only ever reaches the annual plans, so while a
     * sale is running the yearly saving is far larger than the badge admits —
     * 37% rather than 17% at a quarter off. Both numbers are already on the
     * cards, so neither has to be typed anywhere.
     */
    function yearlySavingPercent(introPercentage) {
      var card = root.querySelector('[data-od-price-annual]');
      if (!card) return null;

      var annual = Number(card.dataset.annualCents);
      var monthlyYear = Number(card.dataset.monthlyYearCents);
      if (!isFinite(annual) || !isFinite(monthlyYear) || monthlyYear <= 0) return null;

      var payable = introPercentage
        ? Math.round(annual * (100 - introPercentage) / 100)
        : annual;

      return Math.round((monthlyYear - payable) / monthlyYear * 100);
    }

    function showYearlySaving(introPercentage) {
      var badge = root.querySelector('[data-od-switch-badge]');
      if (!badge) return;

      var percent = yearlySavingPercent(introPercentage);

      /* Nothing rather than "Save 0%" if the two prices ever meet. */
      if (percent === null || percent <= 0) {
        badge.hidden = true;
        return;
      }

      badge.hidden = false;
      badge.textContent = 'Save ' + percent + '%';
    }

    function showIntroPrice(percentage) {
      var cards = root.querySelectorAll('[data-od-price-annual]');

      for (var i = 0; i < cards.length; i++) {
        var card = cards[i];
        var annual = Number(card.dataset.annualCents);
        var target = card.querySelector('[data-od-intro]');
        var amount = card.querySelector('[data-od-amount]');
        var was = card.querySelector('[data-od-was]');
        var per = card.querySelector('[data-od-per]');
        if (!target || !amount || !was || !per) continue;
        if (!isFinite(annual) || annual <= 0) continue;

        var money = moneyFor(card);

        var first = Math.round(annual * (100 - percentage) / 100);
        if (first >= annual) continue;

        /* The sale price becomes the headline, with the standing price struck
           above it. Leaving the standing price as the large number puts the eye
           on the price nobody pays. */
        was.textContent = money(annual);
        was.hidden = false;
        amount.textContent = money(first);
        per.textContent = 'for your first year';

        var permonth = card.querySelector('[data-od-permonth]');
        if (permonth) permonth.textContent = money(Math.round(first / 12)) + ' a month';

        /* Both totals stay on the page. The discount covers the first year
           only, so the second year must never be a surprise. */
        target.innerHTML =
          'Then <strong>' + money(annual) + ' a year</strong>.' +
          '<span class="od-plans__intro-then">Cancel any time.</span>';
        target.hidden = false;
      }

      showYearlySaving(percentage);
    }

    /*
     * Whether a sale is running, and whether this reader may be quoted it.
     *
     * A member has already had their introductory year: the next thing they
     * will actually be charged is the standing rate, so quoting the sale price
     * to them would be quoting a price no charge of theirs will ever match.
     * Cancelling lifts that, because joining again starts a first year.
     *
     * Kept as state rather than acted on where each answer lands, because the
     * two requests race: the sale can arrive before or after the contract, and
     * whichever is second has to be able to put the page right.
     */
    var salePercentage = 0;
    var introWithheld = root.dataset.odNoIntro === '1';

    function renderIntro() {
      if (introWithheld) return;
      if (!(salePercentage > 0)) return;
      showIntroPrice(salePercentage);
    }

    /*
     * Cancelling stops billing and leaves access running to the end of the paid
     * period, writing nothing to the customer record, so Liquid still renders
     * this page for a member: "Change to Collector" against a contract that can
     * no longer be changed. The contract is the only thing that knows.
     */
    /*
     * Held back while we ask.
     *
     * The promise of a refund is in the markup, so it drew, and the answer
     * arrived a moment later and took it away. A customer who cannot have it
     * still got to read it. Only for somebody signed in: a visitor we have
     * never met is entitled to it and there is nothing to wait for.
     */
    var settled = false;

    function settleGuarantee() {
      if (settled) return;
      settled = true;
      root.classList.remove('is-checking');
    }

    if (window.ODCustomerSignedIn) {
      root.classList.add('is-checking');
      window.setTimeout(settleGuarantee, 4000);
    }

    fetch('/apps/library/membership/guarantee', { credentials: 'same-origin' })
      .then(function (response) { return response.json(); })
      .then(function (data) {
        if (!data) return;

        /* Asked for everybody. This used to sit below the cancelled check, so
           it protected somebody on their way out and nobody else. */
        if (data.guaranteeAvailable === false) {
          /* Withheld rather than removed, so the cards keep their height and
             nothing jumps when the answer lands. */
          var notes = root.querySelectorAll('[data-od-trial]');
          for (var n = 0; n < notes.length; n++) notes[n].classList.add('is-withheld');
        }

        if (!data.membership) return;
        if (data.membership.status !== 'cancelled') return;

        var memberOnly = root.querySelectorAll('[data-od-memberonly]');
        for (var m = 0; m < memberOnly.length; m++) memberOnly[m].hidden = true;

        var buyOnly = root.querySelectorAll('[data-od-buyonly]');
        for (var b = 0; b < buyOnly.length; b++) buyOnly[b].hidden = false;

        /* Upgrading is off the table, so the offer of it goes and the
           invitation to join again takes its place. */
        var upgrade = root.querySelector('[data-od-upgrade]');
        if (upgrade) upgrade.hidden = true;

        var rejoin = root.querySelector('[data-od-rejoin]');
        if (rejoin) rejoin.hidden = false;

        /* Liquid showed only the tiers above theirs, which is the right list
           for an upgrade and the wrong one for joining again. All three come
           back. */
        var cards = root.querySelectorAll('[data-od-card]');
        for (var c = 0; c < cards.length; c++) cards[c].hidden = false;

        /* And the billing choice with them. It was withheld because a tier
           change carries the interval over; a new membership does not. */
        var switcher = root.querySelector('[data-od-switch]');
        if (switcher) switcher.hidden = false;

        /* A new first year, so the introductory price applies again. */
        introWithheld = false;
        renderIntro();

        /* The forms were not in the running when the page bound, so the billing
           choice has to be applied to them now. */
        apply(root.dataset.interval || 'annual');

        var when = '';
        if (data.membership.accessEndsAt) {
          var parsed = new Date(data.membership.accessEndsAt);
          if (!isNaN(parsed.getTime())) {
            /* UTC, because the date is a plain day from the contract rather
               than a moment. Formatted in the reader's own zone it slips to the
               day before for anybody west of Greenwich. */
            when = parsed.toLocaleDateString(undefined, {
              year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC'
            });
          }
        }

        var current = root.querySelector('[data-od-current-text]');
        if (current) {
          current.textContent = when
            ? 'Your membership is cancelled and your access ends on ' + when + '. A cancelled membership cannot be changed, so joining again means starting a new one.'
            : 'Your membership is cancelled. A cancelled membership cannot be changed, so joining again means starting a new one.';
        }

      })
      .then(settleGuarantee, settleGuarantee)
      .catch(function () {
        /* The page stands as Liquid rendered it. A failed request is not
           evidence that somebody has cancelled. */
      });

    /* The standing saving first, worked out from the prices actually on the
       cards. If a sale is running, the fetch below raises it. */
    showYearlySaving(0);

    fetch('/apps/library/active-sale', { credentials: 'same-origin' })
      .then(function (response) { return response.json(); })
      .then(function (data) {
        /* Only the membership sale, never the storewide one. The storewide
           percentage cannot reach a subscription, so quoting it here would
           promise a saving no charge could honour. */
        var sale = data && data.membershipSale;
        var value = Number(sale && sale.percentage);

        if (isFinite(value) && value > 0 && value < 100) {
          salePercentage = value;
          renderIntro();
        }
      })
      .catch(function () {
        /* No sale shown. The standing prices on the page are always correct,
           so failing quietly leaves a page that undersells rather than one
           that misquotes. */
      });
  })();
    return true;
  }

  /*
   * Watching the document, not counting the seconds.
   *
   * The theme dispatches pushstate when the link is pressed, before it has
   * fetched the new page, and od:after-swap does not fire on every route. A
   * window of retries after pushstate therefore expires while the old page is
   * still on screen, and nothing runs again once the new markup lands — the
   * page arrives looking finished and does nothing, which is exactly the bug
   * this file was created to fix.
   *
   * An observer has no window to get wrong. It is also what od-sale-display.js
   * does, and the reason a product page keeps its sale price across a
   * transition while this page lost everything.
   */
  var scheduled = false;

  function schedule() {
    if (scheduled) return;
    scheduled = true;

    /*
     * A frame or a timer, whichever comes first.
     *
     * requestAnimationFrame does not fire in a background tab, and the flag
     * above latches while one is pending, so a page opened in a tab nobody is
     * looking at never initialised: the billing toggle dead, the cart forms
     * unclaimed, and the refund promise on screen without having been checked.
     * It recovered when the tab was focused, which is late.
     */
    var run = function () {
      if (!scheduled) return;
      scheduled = false;
      init();
    };

    window.requestAnimationFrame(run);
    window.setTimeout(run, 50);
  }

  schedule();

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", schedule);
  }

  new MutationObserver(schedule).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  /* Kept as well: a swap that replaces nothing this observer can see still
     announces itself. init() is guarded, so running twice costs a querySelector. */
  document.addEventListener("od:after-swap", schedule);
  window.addEventListener("pushstate", schedule);
  window.addEventListener("popstate", schedule);
})();
